import React, { useState, useEffect, useRef } from "react";
import {
  fetchTournamentLevelsList,
  createTournamentLevel,
  updateTournamentLevel,
  deleteTournamentLevel,
} from "../../../api/tournamentControlApi";
import { getCsrfToken } from "../../../../../public/api/authApi";
import { GlobalButton, GlobalTextField } from "../../../../../global/forms/GlobalFormFields";
import { GlobalTableActionButton } from "../../../../../global/GlobalTableActionButton";
import GlobalFormShell from "../../../../../global/forms/GlobalFormShell";
import { GlobalModal } from "../../../../../global/GlobalModal";
import GlobalDataTable from "../../../../../global/GlobalDataTable";
import GlobalSuccessFlashModal from "../../../../../global/GlobalSuccessFlashModal";

const TournamentLevelMaster = ({ onDirectoryChange }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({ id: null, level_name: "", description: "" });
  const [isEditing, setIsEditing] = useState(false);
  const [formError, setFormError] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const alertRef = useRef(null);
  const formSectionRef = useRef(null);

  const loadRows = async () => {
    try {
      setLoading(true);
      const res = await fetchTournamentLevelsList();
      setRows(res?.data || []);
    } catch (err) {
      setError(err.message || "Failed to load levels");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, []);

  const setFormField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormError(null);
  };

  const handleSubmit = async () => {
    setFormError(null);
    setSuccessMsg("");
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }
      if (isEditing) {
        await updateTournamentLevel(
          formData.id,
          { level_name: formData.level_name.trim(), description: formData.description },
          csrfToken,
        );
        setSuccessMsg("Level updated.");
      } else {
        await createTournamentLevel(
          { level_name: formData.level_name.trim(), description: formData.description },
          csrfToken,
        );
        setSuccessMsg("Level created.");
      }
      setFormData({ id: null, level_name: "", description: "" });
      setIsEditing(false);
      await loadRows();
      onDirectoryChange?.();
    } catch (err) {
      setFormError(err.message || "Save failed");
      setTimeout(() => alertRef.current?.focus(), 0);
    }
  };

  const ALERT_ID = "ms-tour-level-alert";

  if (loading) return <p className="ub-loading">Loading tournament levels…</p>;
  if (error) return <p className="ub-error">Error: {error}</p>;

  return (
    <div className="ub-master-section">
      <GlobalSuccessFlashModal message={successMsg} onDismiss={() => setSuccessMsg("")} />
      <h3 className="ub-section__subhead" style={{ margin: "0 0 1rem 0" }}>
        Tournament level master
      </h3>
      <div ref={formSectionRef} style={{ scrollMarginTop: "1rem" }}>
        <GlobalFormShell
          legend={isEditing ? "Edit level" : "Add level"}
          showAlert={!!formError}
          alertText={formError}
          alertId={ALERT_ID}
          formAlertRef={alertRef}
          actions={
            <>
              {isEditing ? (
                <GlobalButton
                  variant="ghost"
                  onClick={() => {
                    setIsEditing(false);
                    setFormData({ id: null, level_name: "", description: "" });
                  }}
                >
                  Cancel Edit
                </GlobalButton>
              ) : null}
              <GlobalButton variant="primary" onClick={handleSubmit}>
                {isEditing ? "Save changes" : "Create level"}
              </GlobalButton>
            </>
          }
        >
          <GlobalTextField
            id="tour_level_name"
            label="Level name"
            value={formData.level_name}
            onChange={(v) => setFormField("level_name", v)}
            required
          />
          <GlobalTextField
            id="tour_level_desc"
            label="Description"
            value={formData.description}
            onChange={(v) => setFormField("description", v)}
          />
        </GlobalFormShell>
      </div>

      <GlobalDataTable
        wrapClassName="ub-table-wrap"
        tableClassName="ub-table"
        tableAriaLabel="Tournament levels"
        headers={[
          { key: "n", label: "Level" },
          { key: "d", label: "Description" },
          {
            key: "a",
            label: "Actions",
            className: "ub-table__col-actions",
          },
        ]}
      >
        {rows.length === 0 ? (
          <tr>
            <td colSpan={3} style={{ textAlign: "center", padding: "1rem", color: "#6b7280" }}>
              No levels yet.
            </td>
          </tr>
        ) : (
          rows.map((r) => (
            <tr key={r.id}>
              <td>{r.level_name}</td>
              <td>{r.description || "—"}</td>
              <td className="ub-table__col-actions">
                <div className="ub-row-actions">
                  <GlobalTableActionButton
                    action="edit"
                    onClick={() => {
                      setFormData({
                        id: r.id,
                        level_name: r.level_name,
                        description: r.description || "",
                      });
                      setIsEditing(true);
                    }}
                  />
                  <GlobalTableActionButton
                    action="delete"
                    onClick={() => {
                      setItemToDelete(r);
                      setShowDelete(true);
                    }}
                  />
                </div>
              </td>
            </tr>
          ))
        )}
      </GlobalDataTable>

      <GlobalModal
        open={showDelete}
        onClose={() => !isDeleting && setShowDelete(false)}
        title="Remove level"
        footer={
          <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
            <GlobalButton onClick={() => setShowDelete(false)} disabled={isDeleting}>
              Cancel
            </GlobalButton>
            <GlobalButton
              variant="danger"
              loading={isDeleting}
              onClick={async () => {
                if (!itemToDelete) return;
                setIsDeleting(true);
                try {
                  let csrfToken = localStorage.getItem("csrf-token");
                  if (!csrfToken) {
                    csrfToken = await getCsrfToken();
                    localStorage.setItem("csrf-token", csrfToken);
                  }
                  await deleteTournamentLevel(itemToDelete.id, csrfToken);
                  setShowDelete(false);
                  setItemToDelete(null);
                  setSuccessMsg("Level removed.");
                  await loadRows();
                  onDirectoryChange?.();
                } catch (e) {
                  alert(e.message || "Delete failed");
                } finally {
                  setIsDeleting(false);
                }
              }}
            >
              Confirm
            </GlobalButton>
          </div>
        }
      >
        <p>
          Deactivate <b>{itemToDelete?.level_name}</b>? It will no longer appear in tournament forms.
        </p>
      </GlobalModal>
    </div>
  );
};

export default TournamentLevelMaster;
